import type { PlaceOrderRequest } from 'apiConnector/secondary-market/cp-order/model';
import { createSelector } from 'reselect';
import * as fromSecondaryMarket from 'state/secondaryMarket/secondaryMarket.selectors';
import { toBcsProductKey } from 'state/secondaryMarket/utils';
import type { AppState } from 'state/state.model';
import { isSecondaryMarketTradeEnabledForUser } from 'state/user';
import type { WorkspaceType } from 'state/workspaces/model';
import * as fromSecondaryPostEventsWorkspace from 'state/workspaces/secondary-market/post-event/secondaryMarketPostEvent.selector';
import type { BcsProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.model';
import * as fromSecondaryQuotesWorkspace from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.selector';

import { computeCashAmountPerQuantityUnit } from './cpOrder.utils';
import { type GetValidationErrorsFunc, getAmendOrderRequestOrderDetails } from './cpOrderForm/cpOrderForm.selectors';

export * as amendedOrder from './amend/amendedOrder.selector';
export * as form from './cpOrderForm/cpOrderForm.selectors';
export * as formInput from './cpOrderForm/input/cpOrderInputForm.selectors';
export * as order from './order/order.selector';
export * as referential from './referential/referential.selector';

export type IsPlaceOrderEnabledFunc = (appState: AppState, tabId: string) => boolean;

export const makeIsReadyToPlaceOrder: (
  getValidationError: GetValidationErrorsFunc,
) => IsPlaceOrderEnabledFunc = getValidationErrors =>
  createSelector(
    [
      (appState: AppState, tabId: string) => getValidationErrors(appState, tabId),
      (appState: AppState, _tabId: string) => isSecondaryMarketTradeEnabledForUser(appState),
      (appState: AppState, tabId: string) => fromSecondaryMarket.cpOrder.form.getFormTradeWay(appState, tabId),
      (appState: AppState, tabId: string) =>
        fromSecondaryMarket.cpOrder.referential.product.getAllowedTradeWays(appState, tabId),
    ],
    (formErrors, hasSecondaryMarketTradeRights, tradeWay, allowedTradeWays) => {
      if (formErrors.filter(e => e.isMandatory).length !== 0 || !hasSecondaryMarketTradeRights) {
        return false;
      }
      return tradeWay !== undefined && allowedTradeWays.includes(tradeWay);
    },
  );

export function getPlaceOrderRequest(state: AppState, tabId: string): PlaceOrderRequest {
  const bcsProductKey = toBcsProductKey(fromSecondaryQuotesWorkspace.getProductKey(state, tabId))!;
  const fileCode = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductFileCode(state, tabId);
  const priceType =
    fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductPriceType(state, tabId) ?? 'Percent';
  return {
    product: {
      bcsKey: bcsProductKey,
      fileCode,
      priceType,
    },
    orderDetails: fromSecondaryMarket.cpOrder.form.getPlaceOrderRequestOrderDetails(state, tabId)!,
  };
}

export function getAmendOrderRequest(state: AppState, tabId: string): Partial<PlaceOrderRequest> {
  const bcsProductKey = fromSecondaryPostEventsWorkspace.getBcsProductKey(state, tabId)!;
  const fileCode = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductFileCode(state, tabId);
  const priceType =
    fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductPriceType(state, tabId) ?? 'Percent';
  return {
    product: {
      bcsKey: bcsProductKey,
      fileCode,
      priceType,
    },
    orderDetails: getAmendOrderRequestOrderDetails(state, tabId)!,
  };
}

export function getCashAmountPerQuantityUnit(state: AppState, tabId: string) {
  const denomination = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductDenomination(state, tabId);
  const tradeWay = fromSecondaryMarket.cpOrder.form.getFormTradeWay(state, tabId);
  const bid = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductBid(state, tabId);
  const ask = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductAsk(state, tabId);
  const priceType = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductPriceType(state, tabId);
  return computeCashAmountPerQuantityUnit(tradeWay, { bid, ask }, denomination, priceType!);
}

export function getBcsProductKeyFormCpOrderTab(
  state: AppState,
  workspaceType: WorkspaceType,
  tabId: string,
): BcsProductKey | undefined {
  if (workspaceType === 'secondaryMarketQuotes') {
    const key = fromSecondaryQuotesWorkspace.getProductKey(state, tabId);
    return toBcsProductKey(key);
  }
  if (workspaceType === 'secondaryMarketPostEvents') {
    return fromSecondaryPostEventsWorkspace.getBcsProductKey(state, tabId);
  }
}
